// components/TelegramChat.jsx
//
// Chat de soporte del USUARIO (el admin se llama desde Telegram y responde).
//
// Lo que estaba roto:
//  - El historial se pedía a `${process.env.REACT_APP_API_URL}/messages/history`.
//   Esa variable no está definida en el build, así que la URL quedaba
//    "undefined/messages/history" y nunca cargaba nada.
//  - El token se mandaba con `localStorage.getItem('token')` SIN parsear: en el
//    storage está con JSON.stringify (va entrecomillado), así que el header
//    era `Bearer "eyJ..."` y el backend respondía 401.
//  - El botón de enviar estaba bloqueado si el socket no estaba conectado, así
//    que sin socket no se podía escribir nada.
//
// Ahora todo va por HTTP (funciona siempre) y el socket solo suma la
// actualización en vivo.
import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { AuthContext } from '../context/authContext';
import { normalizarEstado, ETIQUETA_ESTADO, TEXTO_ESTADO, claseBordeEstado } from '../utils/estadoMensaje';
import { etiquetaAdminRemitente } from '../utils/remitenteMensaje';

// Sin socket no hay eventos: se refresca cada minuto como red de seguridad.
const INTERVALO_RESPALDO_MS = 60000;
// El chat se marca leído un rato después de abrirlo, para que el resaltado de
// los mensajes pendientes se vea (antes se marcaba en el acto y no se llegaba
// a ver el borde).
const DEMORA_MARCAR_LEIDOS_MS = 5000;

const tokenDeStorage = () => {
	try {
		const raw = localStorage.getItem('token');
		return raw ? JSON.parse(raw) : null;
	} catch (e) {
		return null;
	}
};

const TelegramChat = ({ user, userId, socket, isSocketConnected }) => {
	const auth = useContext(AuthContext);
	const token = auth?.token || tokenDeStorage();

	const [roomName, setRoomName] = useState('');
	const [messages, setMessages] = useState([]);
	const [newMessage, setNewMessage] = useState('');
	const [isLoading, setIsLoading] = useState(true);
	const [errorMsg, setErrorMsg] = useState('');
	// Presencia del ADMIN (no la del socket de este usuario: el indicador
	// decía "Conectado" siempre, porque el socket propio está conectado
	// aunque el admin no esté en línea).
	const [adminOnline, setAdminOnline] = useState(false);
	const [cola, setCola] = useState(0);
	// Respuestas del soporte que el usuario todavía no vio (las cuenta el
	// backend: mensajes del admin con estado distinto de 'leido').
	const [respuestasSinLeer, setRespuestasSinLeer] = useState(0);
	// Aviso de que el soporte le respondió (se muestra al abrir el chat).
	const [avisoSoporte, setAvisoSoporte] = useState('');
	// El admin está escribiendo.
	const [adminEscribiendo, setAdminEscribiendo] = useState(false);
	const messagesEndRef = useRef(null);

	useEffect(() => {
		if (userId) {
			setRoomName(`user_${userId}`);
		}
	}, [userId]);

	useEffect(() => {
		if (socket && roomName) {
			socket.emit('joinRoom', roomName);
		}
	}, [socket, roomName]);

	const scrollToBottom = useCallback(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, []);

	useEffect(() => {
		scrollToBottom();
	}, [messages, scrollToBottom]);

	// ------------------------------------------------------------------
	// Historial (siempre por HTTP)
	// ------------------------------------------------------------------
	const loadMessageHistory = useCallback(async () => {
		try {
			const response = await fetch('/api/messages/history', {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
				},
			});

			if (!response.ok) {
				const detalle = await response.json().catch(() => ({}));
				setErrorMsg(detalle?.error || `No se pudo cargar la conversación (${response.status})`);
				return;
			}

			const data = await response.json();
			setMessages(Array.isArray(data?.messages) ? data.messages : []);
			setErrorMsg('');
		} catch (error) {
			console.error('Error al cargar historial:', error);
			setErrorMsg('No se pudo cargar la conversación');
		} finally {
			setIsLoading(false);
		}
	}, [token]);

	useEffect(() => {
		loadMessageHistory();
	}, [loadMessageHistory]);

	// ------------------------------------------------------------------
	// Presencia del admin y cola de mensajes
	// ------------------------------------------------------------------
	const cargarEstado = useCallback(async () => {
		try {
			const response = await fetch('/api/chat/estado', {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
				},
			});

			if (!response.ok) return;

			const data = await response.json();
			setAdminOnline(!!data?.adminOnline);
			setCola(Number(data?.cola) || 0);
			setRespuestasSinLeer(Number(data?.respuestasSinLeer) || 0);
		} catch (error) {
			console.error('Error al cargar el estado del chat:', error);
		}
	}, [token]);

	useEffect(() => {
		cargarEstado();
	}, [cargarEstado]);

	// ------------------------------------------------------------------
	// Acuse de lectura (el usuario abre el chat)
	// ------------------------------------------------------------------
	// Al abrir la conversación, las respuestas del admin que todavía no vio
	// pasan a 'leido'. Sin esto quedaban en 'entregado' para siempre: el admin
	// no sabía si la había leído y el usuario no tenía forma de cerrar el
	// ciclo (la respuesta se quedaba esperando una confirmación que nunca
	// llegaba).
	const marcarLeidos = useCallback(async () => {
		try {
			const response = await fetch('/api/chat/leidos', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
				},
			});
			if (!response.ok) return;

			const data = await response.json().catch(() => ({}));
			const marcados = Number(data?.mensajes) || 0;

			// Si había respuestas sin ver, se avisa: el usuario sabe que el
			// soporte le contestó (y el admin se entera de que la leyó).
			if (marcados > 0) {
				setAvisoSoporte(
					marcados === 1
						? 'El soporte te respondió.'
						: `El soporte te respondió ${marcados} veces.`
				);
			}
			setRespuestasSinLeer(0);
		} catch (error) {
			console.error('Error al marcar las respuestas como leídas:', error);
		}
	}, [token]);

	useEffect(() => {
		// Se espera un momento antes de marcar leído: si no, el borde de los
		// mensajes pendientes desaparece antes de que el usuario lo vea.
		if (isLoading) return undefined;
		const timer = setTimeout(() => { marcarLeidos(); }, DEMORA_MARCAR_LEIDOS_MS);
		return () => clearTimeout(timer);
	}, [isLoading, marcarLeidos]);

	// ------------------------------------------------------------------
	// Mensajes en vivo (el admin responde desde la web o desde Telegram)
	// ------------------------------------------------------------------
	useEffect(() => {
		if (!socket) return;

		const handleNewMessage = (message) => {
			if (!message) return;
			// Solo los mensajes de ESTE usuario.
			if (userId && String(message.user_id) !== String(userId)) return;

			setMessages((prev) => {
				if (message.id && prev.some((m) => m.id === message.id)) return prev;
				return [...prev, message];
			});

			// Si la respuesta del admin llega con el chat abierto, el usuario la
			// está viendo: se marca leída en el acto para que el borde de "sin
			// leer" no quede pegado (y el admin sepa que ya la vio).
			const esDelAdmin = message.from_sender === 'admin' || message.sender === 'admin';
			if (esDelAdmin) marcarLeidos();
		};

		// El admin se conectó o se desconectó: cambia el indicador y la cola.
		const handlePresencia = (payload) => {
			setAdminOnline(!!payload?.adminOnline);
			cargarEstado();
		};

		// El admin abrió la conversación: los mensajes dejan la cola.
		const handleEstadoMensajes = (payload) => {
			const ids = new Set((payload?.ids || []).map(Number));
			if (!ids.size) return;
			setMessages((prev) => prev.map((m) => (ids.has(Number(m.id)) ? { ...m, estado: payload.estado } : m)));
			cargarEstado();
		};

		// El admin está escribiendo.
		const handleEscribiendo = (payload) => {
			setAdminEscribiendo(!!payload?.escribiendo);
		};

		// Reconexión del socket: los eventos que pasaron mientras estaba caído no
		// llegan solos, así que se resincroniza una vez (historial + estado).
		const handleConectar = () => {
			loadMessageHistory();
			cargarEstado();
		};

		socket.on('new-message', handleNewMessage);
		socket.on('chat_presencia', handlePresencia);
		socket.on('chat_mensajes_estado', handleEstadoMensajes);
		socket.on('chat_escribiendo', handleEscribiendo);
		socket.on('connect', handleConectar);

		return () => {
			socket.off('new-message', handleNewMessage);
			socket.off('chat_presencia', handlePresencia);
			socket.off('chat_mensajes_estado', handleEstadoMensajes);
			socket.off('chat_escribiendo', handleEscribiendo);
			socket.off('connect', handleConectar);
		};
	}, [socket, userId, cargarEstado, marcarLeidos, loadMessageHistory]);

	// Respaldo SOLO sin socket: con el socket conectado, el historial y el
	// estado se mantienen con los eventos (new-message / chat_mensajes_estado /
	// chat_presencia) y no hace falta polling.
	useEffect(() => {
		const timer = setInterval(() => {
			if (!socket?.connected) {
				loadMessageHistory();
				cargarEstado();
			}
		}, INTERVALO_RESPALDO_MS);

		return () => clearInterval(timer);
	}, [socket, loadMessageHistory, cargarEstado]);

	// ------------------------------------------------------------------
	// Indicador de "escribiendo"
	// ------------------------------------------------------------------
	// Avisa al admin (con throttling) mientras el usuario escribe, para que
	// en el panel aparezca "está escribiendo...".
	const ultimoAvisoRef = useRef(0);
	const timerAvisoRef = useRef(null);

	const avisarEscribiendo = useCallback(() => {
		if (!socket) return;

		const ahora = Date.now();
		if (ahora - ultimoAvisoRef.current < 2000) return;
		ultimoAvisoRef.current = ahora;

		socket.emit('chat_escribiendo');

		if (timerAvisoRef.current) clearTimeout(timerAvisoRef.current);
		timerAvisoRef.current = setTimeout(() => {
			socket.emit('chat_dejo_de_escribir');
		}, 3000);
	}, [socket]);

	useEffect(() => {
		return () => {
			if (timerAvisoRef.current) clearTimeout(timerAvisoRef.current);
			socket?.emit('chat_dejo_de_escribir');
		};
	}, [socket]);

	// ------------------------------------------------------------------
	// Envío
	// ------------------------------------------------------------------
	const sendMessage = async (e) => {
		e.preventDefault();

		if (!newMessage.trim()) return;

		const texto = newMessage.trim();

		try {
			const response = await fetch('/api/messages/send', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
				},
				body: JSON.stringify({ message: texto }),
			});

			if (!response.ok) {
				const detalle = await response.json().catch(() => ({}));
				setErrorMsg(detalle?.error || 'No se pudo enviar el mensaje');
				return;
			}

			const data = await response.json();
			// El backend devuelve el mensaje guardado (con id y timestamp reales).
			const nuevo = data?.message || {
				user_id: userId,
				from_sender: 'user',
				message: texto,
				timestamp: new Date().toISOString(),
			};

			setMessages((prev) => {
				if (nuevo.id && prev.some((m) => m.id === nuevo.id)) return prev;
				return [...prev, nuevo];
			});
			setNewMessage('');
			setErrorMsg('');
		} catch (error) {
			console.error('Error al enviar el mensaje:', error);
			setErrorMsg('No se pudo enviar el mensaje');
		}
	};

	const formatTime = (timestamp) => {
		if (!timestamp) return '';
		return new Date(timestamp).toLocaleTimeString('es-ES', {
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	if (isLoading) {
		return (
			<div className="flex flex-col h-[500px] bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
				<div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
					Cargando mensajes...
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col h-[500px] bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
			{/* Encabezado del chat */}
			<div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
				<div className="flex justify-between items-center">
					<div className="flex items-center space-x-2">
						<h3 className="text-lg font-semibold text-gray-900 dark:text-white">Soporte</h3>
						{/* Presencia del ADMIN (no la del socket propio). */}
						<span className={`inline-block w-2 h-2 rounded-full ${adminOnline ? 'bg-green-500' : 'bg-gray-400'} mr-1`}></span>
						<span className={`text-sm ${adminOnline ? 'text-green-700 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
							{adminOnline ? 'Soporte conectado' : 'Soporte offline'}
						</span>
					</div>
					<div className="text-sm text-gray-500 dark:text-gray-400">
						Usuario: {user}
					</div>
				</div>
				{/* Cola de mensajes: lo que el usuario escribió y el admin todavía
				    no abrió. */}
				{cola > 0 && (
					<div className="mt-2 text-xs px-2 py-1 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 inline-block">
						{cola === 1 ? '1 mensaje en cola' : `${cola} mensajes en cola`}
						{!adminOnline && ' — el admin está offline, se entrega cuando se conecte'}
					</div>
				)}
				{/* El soporte le respondió (o le está contestando ahora). */}
				{(avisoSoporte || respuestasSinLeer > 0) && (
					<div className="mt-2 text-xs px-2 py-1 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 inline-block">
						{avisoSoporte || 'El soporte te respondió'}
					</div>
				)}
			</div>

			{errorMsg && (
				<div className="px-4 py-2 bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-sm">
					{errorMsg}
				</div>
			)}

			{/* Contenedor de mensajes */}
			<div className="flex-1 p-4 overflow-y-auto bg-white dark:bg-gray-800">
				{messages.length === 0 ? (
					<div className="flex items-center justify-center h-full">
						<p className="text-gray-500 dark:text-gray-400">No hay mensajes aún. ¡Envía un mensaje para comenzar!</p>
					</div>
				) : (
					<div className="space-y-3">
						{messages.map((message, index) => {
							// el resto son mensajes del usuario (derecha).
							const esAdmin = message.from_sender === 'admin' || message.sender === 'admin';
							// Estado de entrega de los mensajes propios (colores y leyenda
							// compartidos con el panel del admin).
							const estado = normalizarEstado(message.estado);
							const etiqueta = ETIQUETA_ESTADO[estado];
							const claseEstado = TEXTO_ESTADO[estado];
							// Respuesta del admin que el usuario todavía no leyó: en cola
							// punteado, entregado sólido (leído no lleva borde).
							const claseSinLeer = esAdmin ? claseBordeEstado(estado) : '';
							return (
								<div key={message.id ?? index} className={`flex ${esAdmin ? 'justify-start' : 'justify-end'}`}>
									<div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${esAdmin ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white' : 'bg-blue-500 text-white'} ${claseSinLeer}`}>
										{esAdmin && (
											<p className="text-[11px] font-semibold uppercase tracking-wide mb-0.5 text-gray-500 dark:text-gray-400">
												{etiquetaAdminRemitente(message)}
											</p>
										)}
										<p className="break-words">{message.message}</p>
										<div className={`text-xs mt-1 flex items-center gap-2 ${esAdmin ? 'text-gray-500 dark:text-gray-400' : 'text-blue-100'}`}>
											<span>{formatTime(message.timestamp)}</span>
											{!esAdmin && <span className={claseEstado}>{etiqueta}</span>}
										</div>
									</div>
								</div>
							);
						})}
						{/* Indicador de "escribiendo" del admin. */}
						{adminEscribiendo && (
							<div className="flex justify-start">
								<div className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700">
									<span className="text-sm text-gray-600 dark:text-gray-300 italic">
										El soporte está escribiendo...
									</span>
								</div>
							</div>
						)}
						<div ref={messagesEndRef} />
					</div>
				)}
			</div>

			{/* Formulario de envío */}
			<form onSubmit={sendMessage} className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
				<div className="flex items-center space-x-2">
					<textarea
						value={newMessage}
						onChange={(e) => {
							setNewMessage(e.target.value);
							avisarEscribiendo();
						}}
						placeholder="Escribe tu mensaje..."
						maxLength={1000}
						className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white dark:placeholder-gray-400"
					/>
					<button
						type="submit"
						disabled={!newMessage.trim()}
						className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						<span className="text-lg">➤</span>
					</button>
				</div>
			</form>
		</div>
	);
};

export default TelegramChat;
